import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle, Calendar, Settings, AlertTriangle, X, Pencil, BookOpen, Map as MapIcon, Shield, BarChart3, Activity, ToggleLeft, ToggleRight, BellOff } from 'lucide-react';
import { getPlayers, getScenarios, createScenario, updateScenario, deleteScenario, getGPS, saveGPS, deleteGPS, getLoadScenarios, createLoadScenario, updateLoadScenario, deleteLoadScenario, getOpponents, saveDrillBatch, getPlayerAliases, createPlayerAlias, uploadGkSessions, getGkSessions, deleteGkSession, uploadTechnicalStatsKnvb, getTechnicalStats, deleteTechnicalStats, uploadCmjTests, getTests, deleteTest } from '../api';
import ScenarioTemplateEditor, { DayMetricsTable, emptyDayTargets, POSITIONS, METRICS } from './ScenarioTemplateEditor';
import ScenarioMapModal from './ScenarioMapModal';
import { SCENARIO_DAYS, SCENARIOS, parseDate, getMDForDate as getMDForDateShared } from '../scenarioDays';

// Groups flat gk_sessions rows (one per player per session) into session
// cards keyed by date+title, matching the shape the GK sessions list renders.
function groupGkSessions(rows) {
  const byKey = {};
  (rows || []).forEach(r => {
    const date = r.session_date ? r.session_date.slice(0, 10) : '';
    const key = `${date}__${r.session_title}`;
    if (!byKey[key]) byKey[key] = { key, date, title: r.session_title, players: [] };
    byKey[key].players.push(r);
  });
  return Object.values(byKey).sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Groups flat test_cmj rows (one per player per test date) into date cards,
// same shape/pattern as groupGkSessions above — test_cmj has no session concept
// of its own (it's the same table the manual Physical Test Input CMJ form writes
// to), so the test date is the closest equivalent to a "session" for display.
function groupCmjTests(rows) {
  const byDate = {};
  (rows || []).forEach(r => {
    const date = r.test_date ? r.test_date.slice(0, 10) : '';
    if (!byDate[date]) byDate[date] = { key: date, date, players: [] };
    byDate[date].players.push(r);
  });
  return Object.values(byDate).sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Local-time date add (no UTC round-trip) — matches the convention already used
// in EWMA.js / scenarioDays.js for calendar-day arithmetic.
function addDaysLocal(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const parts = String(timeStr).trim().split(':');
  if (parts.length === 3) {
    const [h, m, s] = parts.map(Number);
    return Math.round(h * 60 + m + s / 60);
  }
  if (parts.length === 2) {
    const [m, s] = parts.map(Number);
    return Math.round(m + s / 60);
  }
  return null;
};

// Builds one player's "load" metrics (the numbers that land in gps_data) from their
// "Entire Session" summary row(s) for the day. Usually there's exactly one, and this is
// a straight passthrough. When two real sessions share the same Session Date + Session
// Title (2026-07-30 — confirmed case: two MD-2 sessions on one day), there are two, and
// their load needs adding together rather than picking one and dropping the other.
// Additive quantities (distances, counts, minutes, sprints) are summed; peak values (max
// speed, max HR) take the max across sessions, not a sum, since a max isn't cumulative;
// average HR is averaged across sessions. HR-zone time breakdowns and dist/min are
// display-only, lower-stakes fields — HR zones come from the first row rather than
// merging time strings, and dist/min is recomputed from the summed TD/minutes so it stays
// a true rate instead of an average-of-rates.
function buildMetricsFromRows(sourceRows) {
  const sum = key => sourceRows.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);
  const max = key => Math.max(...sourceRows.map(r => parseFloat(r[key]) || 0));
  const first = sourceRows[0];
  const totalMinutes = sourceRows.reduce((s, r) => s + (parseTimeToMinutes(r['Total Time']) || 0), 0) || null;
  const totalTd = sum('Total Distance');
  return {
    TD: totalTd,
    RD: sum('Distance Zone 4 - Zone 6 (Absolute)'),
    HID: sum('High Speed Running (Absolute)'),
    SD: sum('Distance Zone 6 (Absolute)'),
    SA: sourceRows.reduce((s, r) => s + (parseInt(r['Accelerations (Absolute)']) || 0) + (parseInt(r['Decelerations (Absolute)']) || 0), 0),
    Expl: sum('Explosive Distance (Absolute)'),
    HMLD: sum('HML Distance'),
    maxSpeed: max('Max Speed'),
    playerMaxSpeed: max('Player Max Speed'),
    minutesPlayed: totalMinutes,
    avgHR: Math.round(sourceRows.reduce((s, r) => s + (parseFloat(r['Average Heart Rate']) || 0), 0) / sourceRows.length) || 0,
    maxHR: Math.round(max('Max Heart Rate')) || 0,
    distPerMin: totalMinutes ? totalTd / totalMinutes : (parseFloat(first['Distance Per Min']) || 0),
    sprints: sum('Sprints'),
    hrZ1: first['Time In Heart Rate Zone 1 (Relative)'] || '',
    hrZ2: first['Time In Heart Rate Zone 2 (Relative)'] || '',
    hrZ3: first['Time In Heart Rate Zone 3 (Relative)'] || '',
    hrZ4: first['Time In Heart Rate Zone 4 (Relative)'] || '',
    hrZ5: first['Time In Heart Rate Zone 5 (Relative)'] || '',
    hrZ6: first['Time In Heart Rate Zone 6 (Relative)'] || '',
  };
}

const TARGETS_RAW = `Scenario\tdaylabel\tpos\tTD Upper\tTD Lower\tRD Upper\tRD Lower\tHID Upper\tHID Lower\tSD Upper\tSD Lower\tSA Upper\tSA Lower\tExpl Upper\tExpl Lower\tHMLD Upper\tHMLD Lower
1A\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+3\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD+4\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-3\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD-1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
1A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
1A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
1A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
1A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
1A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
1B\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1B\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1B\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1B\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1B\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1B\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1B\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD+3\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
1B\tMD+3\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
1B\tMD+3\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
1B\tMD+3\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
1B\tMD+3\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
1B\tMD+3\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
1B\tMD+4\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1B\tMD+4\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1B\tMD+4\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1B\tMD+4\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1B\tMD+4\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1B\tMD+4\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1B\tMD-3\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-3\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-3\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-3\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-3\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-3\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1B\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
1B\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
1B\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
1B\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
1B\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
1B\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
1B\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
1B\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
1B\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
1B\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
1B\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
1B\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
1B\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
1B\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
1B\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
1B\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
1B\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
1B\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
1C\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
1C\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
1C\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
1C\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
1C\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
1C\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
1C\tMD+3\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1C\tMD+3\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1C\tMD+3\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1C\tMD+3\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1C\tMD+3\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1C\tMD+3\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1C\tMD+4\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+4\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+4\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+4\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+4\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD+4\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1C\tMD-3\tCB\t5524\t4564\t632\t484\t174\t119\t37\t14\t87\t72\t564\t403\t738\t523
1C\tMD-3\tFB\t5777\t4772\t611\t467\t303\t208\t77\t29\t111\t92\t720\t514\t1023\t722
1C\tMD-3\tCDM\t6344\t5241\t749\t573\t243\t167\t38\t14\t105\t87\t679\t485\t922\t652
1C\tMD-3\tCAM\t6103\t5042\t844\t645\t234\t161\t36\t13\t105\t87\t679\t485\t913\t646
1C\tMD-3\tST\t5554\t4588\t674\t515\t275\t189\t60\t22\t96\t79\t618\t442\t893\t630
1C\tMD-3\tW\t5711\t4717\t618\t472\t301\t207\t65\t24\t111\t92\t720\t514\t1020\t721
1C\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
1C\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
1C\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
1C\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
1C\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
1C\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
1C\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
1C\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
1C\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
1C\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
1C\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
1C\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
1C\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
1C\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
1C\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
1C\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
1C\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
1C\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
1D\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1D\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1D\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1D\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1D\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1D\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1D\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD+3\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
1D\tMD+3\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
1D\tMD+3\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
1D\tMD+3\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
1D\tMD+3\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
1D\tMD+3\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
1D\tMD+4\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1D\tMD+4\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1D\tMD+4\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1D\tMD+4\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1D\tMD+4\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1D\tMD+4\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1D\tMD-4\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-4\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-4\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-4\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-4\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-4\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
1D\tMD-3\tCB\t5524\t4564\t632\t484\t174\t119\t37\t14\t87\t72\t564\t403\t738\t523
1D\tMD-3\tFB\t5777\t4772\t611\t467\t303\t208\t77\t29\t111\t92\t720\t514\t1023\t722
1D\tMD-3\tCDM\t6344\t5241\t749\t573\t243\t167\t38\t14\t105\t87\t679\t485\t922\t652
1D\tMD-3\tCAM\t6103\t5042\t844\t645\t234\t161\t36\t13\t105\t87\t679\t485\t913\t646
1D\tMD-3\tST\t5554\t4588\t674\t515\t275\t189\t60\t22\t96\t79\t618\t442\t893\t630
1D\tMD-3\tW\t5711\t4717\t618\t472\t301\t207\t65\t24\t111\t92\t720\t514\t1020\t721
1D\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
1D\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
1D\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
1D\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
1D\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
1D\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
1D\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
1D\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
1D\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
1D\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
1D\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
1D\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
1D\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
1D\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
1D\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
1D\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
1D\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
1D\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
1E\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1E\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1E\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1E\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1E\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1E\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1E\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
1E\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
1E\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
1E\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
1E\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
1E\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
1E\tMD+3\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
1E\tMD+3\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
1E\tMD+3\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
1E\tMD+3\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
1E\tMD+3\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
1E\tMD+3\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
1E\tMD-2\tCB\t5524\t4564\t632\t484\t174\t119\t37\t14\t87\t72\t564\t403\t738\t523
1E\tMD-2\tFB\t5777\t4772\t611\t467\t303\t208\t77\t29\t111\t92\t720\t514\t1023\t722
1E\tMD-2\tCDM\t6344\t5241\t749\t573\t243\t167\t38\t14\t105\t87\t679\t485\t922\t652
1E\tMD-2\tCAM\t6103\t5042\t844\t645\t234\t161\t36\t13\t105\t87\t679\t485\t913\t646
1E\tMD-2\tST\t5554\t4588\t674\t515\t275\t189\t60\t22\t96\t79\t618\t442\t893\t630
1E\tMD-2\tW\t5711\t4717\t618\t472\t301\t207\t65\t24\t111\t92\t720\t514\t1020\t721
1E\tMD-1\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
1E\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
1E\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
1E\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
1E\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
1E\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
1E\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
1E\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
1E\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
1E\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
1E\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
1E\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
2A\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
2A\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
2A\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
2A\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
2A\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
2A\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
2A\tMD+3\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
2A\tMD+3\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
2A\tMD+3\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
2A\tMD+3\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
2A\tMD+3\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
2A\tMD+3\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
2A\tMD-3\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-3\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-3\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-3\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-3\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-3\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2A\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
2A\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
2A\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
2A\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
2A\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
2A\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
2A\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
2A\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
2A\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
2A\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
2A\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
2A\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
2A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
2A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
2A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
2A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
2A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
2A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
2B\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
2B\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
2B\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
2B\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
2B\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
2B\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
2B\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2B\tMD+3\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
2B\tMD+3\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
2B\tMD+3\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
2B\tMD+3\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
2B\tMD+3\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
2B\tMD+3\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
2B\tMD-3\tCB\t7304\t6034\t1613\t1233\t429\t295\t87\t32\t126\t104\t816\t583\t1465\t1033
2B\tMD-3\tFB\t7638\t6310\t1557\t1191\t749\t515\t183\t68\t161\t133\t1040\t743\t2105\t1480
2B\tMD-3\tCDM\t8388\t6929\t1910\t1460\t601\t413\t90\t33\t152\t125\t982\t701\t1862\t1311
2B\tMD-3\tCAM\t8069\t6666\t2152\t1646\t578\t397\t85\t32\t152\t125\t982\t701\t1835\t1293
2B\tMD-3\tST\t7344\t6067\t1718\t1314\t679\t467\t143\t53\t138\t114\t894\t638\t1850\t1300
2B\tMD-3\tW\t4845\t4003\t1575\t1204\t743\t511\t154\t57\t161\t133\t1040\t743\t2099\t1476
2B\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
2B\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
2B\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
2B\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
2B\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
2B\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
2B\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
2B\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
2B\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
2B\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
2B\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
2B\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
2B\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
2B\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
2B\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
2B\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
2B\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
2B\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
2C\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
2C\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
2C\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
2C\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
2C\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
2C\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
2C\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
2C\tMD-3\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
2C\tMD-3\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
2C\tMD-3\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
2C\tMD-3\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
2C\tMD-3\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
2C\tMD-3\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
2C\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
2C\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
2C\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
2C\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
2C\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
2C\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
2C\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
2C\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
2C\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
2C\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
2C\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
2C\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
2C\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
2C\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
2C\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
2C\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
2C\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
2C\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
3A\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3A\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
3A\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
3A\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
3A\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
3A\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
3A\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
3A\tMD+3\tCB\t7304\t6034\t1613\t1233\t429\t295\t87\t32\t126\t104\t816\t583\t1465\t1033
3A\tMD+3\tFB\t7638\t6310\t1557\t1191\t749\t515\t183\t68\t161\t133\t1040\t743\t2105\t1480
3A\tMD+3\tCDM\t8388\t6929\t1910\t1460\t601\t413\t90\t33\t152\t125\t982\t701\t1862\t1311
3A\tMD+3\tCAM\t8069\t6666\t2152\t1646\t578\t397\t85\t32\t152\t125\t982\t701\t1835\t1293
3A\tMD+3\tST\t7344\t6067\t1718\t1314\t679\t467\t143\t53\t138\t114\t894\t638\t1850\t1300
3A\tMD+3\tW\t4845\t4003\t1575\t1204\t743\t511\t154\t57\t161\t133\t1040\t743\t2099\t1476
3A\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
3A\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
3A\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
3A\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
3A\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
3A\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
3A\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
3A\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
3A\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
3A\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
3A\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
3A\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
3A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
3A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
3A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
3A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
3A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
3A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
3B\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
3B\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
3B\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
3B\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
3B\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
3B\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
3B\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3B\tMD+3\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
3B\tMD+3\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
3B\tMD+3\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
3B\tMD+3\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
3B\tMD+3\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
3B\tMD+3\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
3B\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
3B\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
3B\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
3B\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
3B\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
3B\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
3B\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
3B\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
3B\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
3B\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
3B\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
3B\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
3B\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
3B\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
3B\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
3B\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
3B\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
3B\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
3C\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
3C\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
3C\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
3C\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
3C\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
3C\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
3C\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
3C\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
3C\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
3C\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
3C\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
3C\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
3C\tMD+3\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD+3\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD+3\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD+3\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD+3\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD+3\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3C\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
3C\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
3C\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
3C\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
3C\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
3C\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
3C\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
3C\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
3C\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
3C\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
3C\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
3C\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
3C\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
3C\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
3C\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
3C\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
3C\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
3C\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
3D\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
3D\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
3D\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
3D\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
3D\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
3D\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
3D\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
3D\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
3D\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
3D\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
3D\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
3D\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
3D\tMD+3\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
3D\tMD+3\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
3D\tMD+3\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
3D\tMD+3\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
3D\tMD+3\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
3D\tMD+3\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
3D\tMD-2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
3D\tMD-1\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
3D\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
3D\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
3D\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
3D\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
3D\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
3D\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
3D\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
3D\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
3D\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
3D\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
3D\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
4A\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4A\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
4A\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
4A\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
4A\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
4A\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
4A\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
4A\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
4A\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
4A\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
4A\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
4A\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
4A\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
4A\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
4A\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
4A\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
4A\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
4A\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
4A\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
4A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
4A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
4A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
4A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
4A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
4A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
4B\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4B\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t650\t462
4B\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t868\t616
4B\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t798\t567
4B\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t679\t485\t794\t564
4B\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t753\t534
4B\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t867\t616
4B\tMD+3\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
4B\tMD+3\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
4B\tMD+3\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
4B\tMD+3\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
4B\tMD+3\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
4B\tMD+3\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
4B\tMD+4\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
4B\tMD+4\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
4B\tMD+4\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
4B\tMD+4\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
4B\tMD+4\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
4B\tMD+4\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
4B\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
4B\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
4B\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
4B\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
4B\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
4B\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
4C\tMD+1\tCB\t8593\t7099\t1897\t1451\t505\t347\t103\t38\t148\t123\t960\t685\t1465\t1033
4C\tMD+1\tFB\t8986\t7423\t1832\t1401\t881\t605\t215\t80\t189\t156\t1224\t874\t2105\t1480
4C\tMD+1\tCDM\t9868\t8152\t2247\t1718\t707\t486\t106\t39\t179\t148\t1155\t825\t1862\t1311
4C\tMD+1\tCAM\t9493\t7842\t2532\t1936\t680\t468\t101\t37\t179\t148\t1155\t825\t1835\t1293
4C\tMD+1\tST\t8640\t7137\t2021\t1546\t799\t549\t169\t62\t163\t134\t1052\t751\t1850\t1300
4C\tMD+1\tW\t8883\t7338\t1853\t1417\t875\t601\t181\t67\t189\t156\t1224\t874\t2099\t1476
4C\tMD+2\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD+2\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD+2\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD+2\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD+2\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD+2\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
4C\tMD-2\tCB\t5524\t4564\t632\t484\t174\t119\t37\t14\t87\t72\t564\t403\t738\t523
4C\tMD-2\tFB\t5777\t4772\t611\t467\t303\t208\t77\t29\t111\t92\t720\t514\t1023\t722
4C\tMD-2\tCDM\t6344\t5241\t749\t573\t243\t167\t38\t14\t105\t87\t679\t485\t922\t652
4C\tMD-2\tCAM\t6103\t5042\t844\t645\t234\t161\t36\t13\t105\t87\t679\t485\t913\t646
4C\tMD-2\tST\t5554\t4588\t674\t515\t275\t189\t60\t22\t96\t79\t618\t442\t893\t630
4C\tMD-2\tW\t5711\t4717\t618\t472\t301\t207\t65\t24\t111\t92\t720\t514\t1020\t721
4C\tMD-1\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
4C\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
4C\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
4C\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
4C\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
4C\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
4C\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
4C\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
4C\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
4C\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
4C\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
4C\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
5A\tMD+1\tCB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+1\tFB\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+1\tCDM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+1\tCAM\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+1\tST\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+1\tW\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0\t0
5A\tMD+2\tCB\t4687\t3872\t548\t419\t85\t59\t7\t3\t87\t72\t564\t403\t652\t475
5A\tMD+2\tFB\t4902\t4049\t529\t405\t149\t102\t15\t6\t111\t92\t720\t514\t831\t606
5A\tMD+2\tCDM\t5383\t4447\t649\t496\t119\t82\t8\t3\t105\t87\t679\t485\t784\t572
5A\tMD+2\tCAM\t5178\t4278\t732\t559\t115\t79\t7\t3\t105\t87\t618\t442\t724\t529
5A\tMD+2\tST\t4713\t3893\t584\t446\t135\t93\t12\t4\t96\t79\t618\t442\t714\t521
5A\tMD+2\tW\t4845\t4003\t535\t409\t148\t101\t13\t5\t111\t92\t720\t514\t831\t606
5A\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
5A\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
5A\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
5A\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
5A\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
5A\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
5A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
5A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
5A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
5A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
5A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
5A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
6A\tMD-2\tCB\t6361\t5255\t717\t548\t284\t195\t66\t24\t87\t72\t564\t403\t849\t598
6A\tMD-2\tFB\t6652\t5495\t692\t529\t495\t341\t139\t51\t111\t92\t720\t514\t1215\t855
6A\tMD-2\tCDM\t7305\t6035\t849\t649\t397\t273\t68\t25\t105\t87\t679\t485\t1077\t758
6A\tMD-2\tCAM\t7028\t5805\t957\t732\t383\t263\t65\t24\t105\t87\t679\t485\t1062\t748
6A\tMD-2\tST\t6396\t5284\t764\t584\t449\t309\t108\t40\t96\t79\t618\t442\t1068\t751
6A\tMD-2\tW\t6576\t5432\t700\t535\t492\t338\t116\t43\t111\t92\t720\t514\t1212\t852
6A\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
6A\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
6A\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
6A\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
6A\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
6A\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
6A\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
6A\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
6A\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
6A\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
6A\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
6A\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535
6B\tMD+1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t118\t97\t564\t403\t612\t436
6B\tMD+1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t150\t124\t720\t514\t802\t571
6B\tMD+1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t142\t117\t679\t485\t746\t531
6B\tMD+1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t142\t117\t679\t485\t743\t529
6B\tMD+1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t129\t107\t618\t442\t693\t493
6B\tMD+1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t150\t124\t720\t514\t802\t571
6B\tMD-1\tCB\t4464\t3688\t590\t451\t47\t33\t22\t8\t79\t65\t510\t364\t557\t397
6B\tMD-1\tFB\t4668\t3856\t570\t436\t83\t57\t46\t17\t100\t83\t650\t464\t733\t521
6B\tMD-1\tCDM\t5126\t4235\t699\t535\t66\t46\t23\t8\t95\t78\t613\t438\t680\t484
6B\tMD-1\tCAM\t4932\t4074\t788\t602\t64\t44\t22\t8\t95\t78\t613\t438\t677\t482
6B\tMD-1\tST\t4488\t3708\t629\t481\t75\t51\t36\t13\t86\t71\t558\t399\t633\t450
6B\tMD-1\tW\t4615\t3812\t576\t441\t82\t56\t39\t14\t100\t83\t650\t464\t732\t521
6B\tMD\tCB\t10675\t10675\t2480\t2480\t592\t592\t136\t136\t128\t128\t1184\t1184\t1776\t1776
6B\tMD\tFB\t11163\t11163\t2395\t2395\t1032\t1032\t285\t285\t185\t185\t1510\t1510\t2542\t2542
6B\tMD\tCDM\t12259\t12259\t2937\t2937\t828\t828\t140\t140\t165\t165\t1425\t1425\t2253\t2253
6B\tMD\tCAM\t11793\t11793\t3310\t3310\t797\t797\t133\t133\t167\t167\t1425\t1425\t2222\t2222
6B\tMD\tST\t10733\t10733\t2642\t2642\t936\t936\t223\t223\t157\t157\t1297\t1297\t2233\t2233
6B\tMD\tW\t11035\t11035\t2422\t2422\t1025\t1025\t239\t239\t187\t187\t1510\t1510\t2535\t2535`;

const parseTargets = () => {
  const lines = TARGETS_RAW.trim().split('\n').slice(1);
  const lookup = {};
  lines.forEach(line => {
    const p = line.split('\t');
    const [scenario, daylabel, pos] = p;
    const n = v => parseFloat(String(v).replace(',', '.')) || 0;
    if (!lookup[scenario]) lookup[scenario] = {};
    if (!lookup[scenario][daylabel]) lookup[scenario][daylabel] = {};
    lookup[scenario][daylabel][pos] = {
      TD:   { upper: n(p[3]),  lower: n(p[4])  },
      RD:   { upper: n(p[5]),  lower: n(p[6])  },
      HID:  { upper: n(p[7]),  lower: n(p[8])  },
      SD:   { upper: n(p[9]),  lower: n(p[10]) },
      SA:   { upper: n(p[11]), lower: n(p[12]) },
      Expl: { upper: n(p[13]), lower: n(p[14]) },
      HMLD: { upper: n(p[15]), lower: n(p[16]) },
    };
  });
  return lookup;
};

const TARGETS = parseTargets();

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };


const emptyScenario = { scenario: '1B', startDate: '', playerId: 'team', intensity: 100, mdMinutes: 90, targetOverrides: {} };

// Minimal inline resolver for an unmatched Technical Stats CSV name — pick the player, save an
// alias (reuses the existing player_name_aliases table/API, same one Settings' "Player Name
// Aliases" panel manages), so this exact CSV name resolves automatically on every future
// import instead of being re-flagged every time.
function UnmatchedNameResolver({ name, players, onResolved }) {
  const [playerId, setPlayerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!playerId) return;
    setSaving(true);
    setError('');
    try {
      await createPlayerAlias({ player_id: playerId, alias_name: name });
      onResolved(name);
    } catch (err) {
      console.error('Failed to save alias:', err);
      setError('Failed to save — that name may already be aliased.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0' }}>
      <span style={{ fontSize: 13, color: '#fca5a5', minWidth: 180 }}>{name}</span>
      <span style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>is…</span>
      <select value={playerId} onChange={e => setPlayerId(e.target.value)}
        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit' }}>
        <option value="">Pick a player…</option>
        {players.map(p => <option key={p.id} value={p.id}>{p.name}{p.active === false ? ' (inactive)' : ''}</option>)}
      </select>
      <button disabled={!playerId || saving} onClick={handleSave}
        style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: playerId ? '#3b82f6' : 'var(--border)', color: playerId ? '#fff' : 'var(--text-dimmed)', cursor: playerId ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
        {saving ? 'Saving…' : 'Save alias'}
      </button>
      {error && <span style={{ fontSize: 11, color: '#fca5a5' }}>{error}</span>}
    </div>
  );
}

export default function GPSUpload() {
  const [players, setPlayers]         = useState([]);
  const [scenarios, setScenarios]     = useState([]);
  const [sessions, setSessions]       = useState([]);
  const [gkSessions, setGkSessions]   = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  // Defaults to on (today's existing behavior) — switched off when backfilling old matches so
  // players/staff don't get paged for every historical game being imported.
  const [sendNotifications, setSendNotifications] = useState(true);
  const [gkUploadStatus, setGkUploadStatus] = useState(null);
  const [gkUploadResult, setGkUploadResult] = useState(null);
  // KNVB Technical Stats import (2026-08-17) — the only Technical Stats source now (SciSports's
  // own techUploadStatus/Result/Error + resolvedAliasNames state lived here before the club
  // fully switched sources; removed along with its upload card). Names resolved via the inline
  // alias form below are tracked locally so the "unmatched" list can show them as resolved
  // without needing to re-upload the same CSV just to refresh it.
  const [techKnvbUploadStatus, setTechKnvbUploadStatus] = useState(null);
  const [techKnvbUploadResult, setTechKnvbUploadResult] = useState(null);
  const [techKnvbUploadError, setTechKnvbUploadError] = useState('');
  const [resolvedAliasNamesKnvb, setResolvedAliasNamesKnvb] = useState(new Set());
  const [technicalStatsSessions, setTechnicalStatsSessions] = useState([]);
  const [cmjTests, setCmjTests] = useState([]);
  const [cmjUploadStatus, setCmjUploadStatus] = useState(null);
  const [cmjUploadResult, setCmjUploadResult] = useState(null);
  // Same per-import tracking as resolvedAliasNames above, just for the CMJ card's own
  // unmatched list (independent Set so resolving a name in one import doesn't affect the other).
  const [resolvedCmjAliasNames, setResolvedCmjAliasNames] = useState(new Set());
  const [showForm, setShowForm]       = useState(false);
  const [showEditPicker, setShowEditPicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState(emptyScenario);
  const [templateEditor, setTemplateEditor] = useState(null); // null | 'new' | templateObj
  const [showScenarioMap, setShowScenarioMap] = useState(false);
  const [opponents, setOpponents] = useState([]);
  const [expandedOverrideDates, setExpandedOverrideDates] = useState(() => new Set());
  const [aliases, setAliases] = useState([]);

  // Resolve a CSV player name to the canonical player record — direct match
  // first, then falls back to a known alias (e.g. Statsports export typos).
  const resolvePlayer = (csvName) => {
    if (!csvName) return null;
    const lower = csvName.toLowerCase();
    // Active-only, matching the backend's own resolvePlayerId — an inactive
    // duplicate (e.g. a renamed player whose old record was retired) must not
    // win over an alias that deliberately redirects this CSV name elsewhere.
    const direct = players.find(p => p.name.toLowerCase() === lower && p.active);
    if (direct) return direct;
    const alias = aliases.find(a => a.alias_name.toLowerCase() === lower);
    if (alias) return players.find(p => String(p.id) === String(alias.player_id)) || null;
    // Last resort: an inactive player with no alias configured (e.g. genuinely
    // departed) still resolves by exact name rather than silently dropping data.
    return players.find(p => p.name.toLowerCase() === lower) || null;
  };

  // Merged lookup maps (built-in + custom from server)
  const allTargets = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.targets; });
    return { ...TARGETS, ...custom };
  }, [customTemplates]);

  const allScenarioDays = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.days; });
    return { ...SCENARIO_DAYS, ...custom };
  }, [customTemplates]);

  const allScenarioNames = useMemo(() => {
    const customNames = customTemplates.map(t => t.name);
    const extra = customNames.filter(n => !SCENARIOS.includes(n));
    return [...SCENARIOS, ...extra];
  }, [customTemplates]);

  // Day-by-day breakdown of the scenario currently being planned in the form —
  // lets the user pick a specific calendar date (e.g. "this Sunday") to override.
  const previewDays = useMemo(() => {
    if (!form.startDate) return [];
    const days = allScenarioDays[form.scenario] || [];
    return days.map((mdLabel, i) => ({ date: addDaysLocal(form.startDate, i), mdLabel }));
  }, [form.startDate, form.scenario, allScenarioDays]);

  // Baseline targets for one day of the form's scenario.
  // MD day: never scaled by training intensity — instead scaled by planned match
  // duration with +10% warmup allowance. All other days use the intensity slider.
  const getBaselineDayTargets = (mdLabel) => {
    const base = allTargets[form.scenario]?.[mdLabel];
    if (!base) return emptyDayTargets();
    let factor;
    if (mdLabel === 'MD') {
      const mdMins = form.mdMinutes ?? 90;
      factor = (mdMins / 90) * 1.1;
    } else {
      factor = (form.intensity ?? 100) / 100;
    }
    const out = {};
    POSITIONS.forEach(pos => {
      out[pos] = {};
      METRICS.forEach(m => {
        const cell = base[pos]?.[m] || { upper: 0, lower: 0 };
        out[pos][m] = { upper: Math.round(cell.upper * factor), lower: Math.round(cell.lower * factor) };
      });
    });
    return out;
  };

  const getDayTargetsForForm = (dateStr, mdLabel) =>
    form.targetOverrides?.[dateStr] || getBaselineDayTargets(mdLabel);

  const updateOverrideCell = (dateStr, mdLabel, pos, metric, bound, value) => {
    setForm(f => {
      const current = f.targetOverrides?.[dateStr] || getBaselineDayTargets(mdLabel);
      const updatedDay = {
        ...current,
        [pos]: { ...current[pos], [metric]: { ...current[pos]?.[metric], [bound]: value } },
      };
      return { ...f, targetOverrides: { ...f.targetOverrides, [dateStr]: updatedDay } };
    });
  };

  const resetOverrideDay = (dateStr) => {
    setForm(f => {
      const next = { ...f.targetOverrides };
      delete next[dateStr];
      return { ...f, targetOverrides: next };
    });
  };

  const toggleOverrideExpanded = (dateStr) => {
    setExpandedOverrideDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  };

  const loadTechnicalStats = () => getTechnicalStats().then(rows => setTechnicalStatsSessions(rows || [])).catch(err => console.error('Failed to load technical stats:', err));
  const loadCmjTests = () => getTests('cmj').then(rows => setCmjTests(rows || [])).catch(err => console.error('Failed to load CMJ tests:', err));

  useEffect(() => {
    const load = async () => {
      try {
        const [p, sc, s, ct, opps, al, gk] = await Promise.all([
          getPlayers(),
          getScenarios(),
          getGPS(),
          getLoadScenarios(),
          getOpponents(),
          getPlayerAliases(),
          getGkSessions(),
        ]);
        setCustomTemplates(ct || []);
        setOpponents(opps || []);
        setAliases(al || []);
        setPlayers(p);
        setGkSessions(groupGkSessions(gk));
        loadTechnicalStats();
        loadCmjTests();
        setScenarios(sc.map(s => ({
          ...s,
          startDate: s.start_date ? s.start_date.slice(0, 10) : '',
          playerId: s.player_id,
        })));
        setSessions(s.map(sess => ({
          ...sess,
          date: sess.session_date ? sess.session_date.slice(0, 10) : '',
          title: sess.session_title,
          players: sess.players.map(p => ({
            ...p,
            playerId: p.player_id,
            mdLabel: p.md_label,
            metrics: {
              TD: p.td, RD: p.rd, HID: p.hid, SD: p.sd,
              SA: p.sa, Expl: p.expl, HMLD: p.hmld,
              maxSpeed: p.max_speed, avgHR: p.avg_hr,
              maxHR: p.max_hr, distPerMin: p.dist_per_min,
              sprints: p.sprints, hrZ1: p.hr_z1, hrZ2: p.hr_z2,
              hrZ3: p.hr_z3, hrZ4: p.hr_z4, hrZ5: p.hr_z5, hrZ6: p.hr_z6,
            },
          })),
        })));
      } catch (err) {
        console.error('Failed to load GPS data:', err);
      }
    };
    load();
  }, []);

  const handleDeleteGkSession = async (session) => {
    if (!window.confirm(`Delete "${session.title || 'Session'}" (${session.date})? This removes ${session.players.length} player row(s).`)) return;
    try {
      await deleteGkSession(session.date, session.title);
      setGkSessions(prev => prev.filter(x => x.key !== session.key));
    } catch (err) {
      console.error('Failed to delete GK session:', err);
    }
  };

  const handleDeleteSession = async (id) => {
    try {
      await deleteGPS(id);
      setSessions(prev => prev.filter(x => x.id !== id));
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const handleDeleteTechnicalStats = async (session) => {
    if (!window.confirm(`Delete technical stats for "${session.session_title || session.opponent_name || 'Match'}" (${session.session_date?.slice(0, 10)})? This removes ${session.row_count} stat row(s) for ${session.player_count} player(s).`)) return;
    try {
      await deleteTechnicalStats(session.match_id);
      setTechnicalStatsSessions(prev => prev.filter(x => x.match_id !== session.match_id));
    } catch (err) {
      console.error('Failed to delete technical stats:', err);
    }
  };

  const handleDeleteCmjGroup = async (group) => {
    if (!window.confirm(`Delete CMJ tests for ${group.date}? This removes ${group.players.length} player result(s).`)) return;
    try {
      await Promise.all(group.players.map(r => deleteTest('cmj', r.player_id, group.date)));
      setCmjTests(prev => prev.filter(r => (r.test_date ? r.test_date.slice(0, 10) : '') !== group.date));
    } catch (err) {
      console.error('Failed to delete CMJ tests:', err);
    }
  };

  const getMDForDate = (sc, dateStr) => getMDForDateShared(sc, dateStr, allScenarioDays);

  // ── Load scenario template handlers ────────────────────────────────────────
  const handleSaveTemplate = async (data) => {
    if (templateEditor && templateEditor !== 'new') {
      const updated = await updateLoadScenario(templateEditor.id, data);
      setCustomTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
    } else {
      const created = await createLoadScenario(data);
      setCustomTemplates(prev => [...prev, created]);
    }
    setTemplateEditor(null);
  };

  const handleDeleteTemplate = async (id) => {
    await deleteLoadScenario(id);
    setCustomTemplates(prev => prev.filter(t => t.id !== id));
  };

  const openAdd = () => { setForm(emptyScenario); setEditingId(null); setExpandedOverrideDates(new Set()); setShowEditPicker(false); setShowForm(true); };
  const openEdit = sc => { setForm({ scenario: sc.scenario, startDate: sc.startDate, playerId: sc.playerId, intensity: sc.intensity ?? 100, mdMinutes: sc.md_minutes ?? 90, targetOverrides: sc.target_overrides || {} }); setEditingId(sc.id); setExpandedOverrideDates(new Set()); setShowEditPicker(false); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setShowEditPicker(false); setEditingId(null); setForm(emptyScenario); setExpandedOverrideDates(new Set()); };

  const saveForm = async () => {
    if (!form.startDate) { alert('Please select a start date.'); return; }
    try {
      if (editingId) {
        const updated = await updateScenario(editingId, {
          scenario: form.scenario,
          start_date: form.startDate,
          player_id: form.playerId,
          intensity: form.intensity ?? 100,
          md_minutes: form.mdMinutes ?? 90,
          target_overrides: form.targetOverrides || {},
        });
        setScenarios(prev => prev.map(s => s.id === editingId ? { ...updated, startDate: updated.start_date?.slice(0,10), playerId: updated.player_id, intensity: updated.intensity ?? form.intensity ?? 100, md_minutes: updated.md_minutes ?? form.mdMinutes ?? 90 } : s));
      } else {
        const created = await createScenario({
          scenario: form.scenario,
          start_date: form.startDate,
          player_id: form.playerId,
          intensity: form.intensity ?? 100,
          md_minutes: form.mdMinutes ?? 90,
          target_overrides: form.targetOverrides || {},
        });
        setScenarios(prev => [...prev, { ...created, startDate: created.start_date?.slice(0,10), playerId: created.player_id }]);
      }
      closeForm();
    } catch (err) {
      console.error('Failed to save scenario:', err);
      alert('Failed to save scenario.');
    }
  };

  const removeScenario = async id => {
    try {
      await deleteScenario(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete scenario:', err);
    }
  };

  const handleCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadStatus('processing');
    const reader = new FileReader();
    reader.onload = async evt => {
      const convertDate = str => {
        if (!str) return null;
        if (str.includes('/')) {
          const [d, m, y] = str.split('/');
          return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
        }
        return str;
      };
      try {
        const lines = evt.target.result.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
        const rows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.replace(/"/g, '').trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
          return obj;
        });

        const sessionMap = {};
        rows.forEach(row => {
          const firstName = (row['Player First Name'] || '').trim();
          const lastName  = (row['Player Last Name']  || '').trim();
          const fullName  = `${firstName} ${lastName}`.trim();
          if (!fullName) return;
          const date  = (row['Session Date'] || '').trim();
          const title = (row['Session Title'] || '').trim();
          const key   = `${date}__${title}`;
          if (!sessionMap[key]) sessionMap[key] = { date, title, playerMap: {} };
          const td = parseFloat(row['Total Distance']) || 0;
          // Two genuinely separate sessions sharing the same Session Date + Session Title
          // (e.g. two MD-2 sessions run the same day, confirmed 2026-07-30) land in this same
          // key/player bucket, each contributing its own "Entire Session" summary drill row.
          // Those are collected separately (entireSessionRows) and summed below to build that
          // player's load for the day, instead of the old highest-TD-wins pick that kept only
          // one session and silently discarded the other.
          const isEntireSession = (row['Drill Title'] || '').toLowerCase().includes('entire session');
          const existing = sessionMap[key].playerMap[fullName];
          if (!existing) {
            sessionMap[key].playerMap[fullName] = {
              row, td, drillRows: [row],
              entireSessionRows: isEntireSession ? [row] : [],
            };
          } else {
            existing.drillRows.push(row);
            if (isEntireSession) {
              existing.entireSessionRows.push(row);
            } else if (!existing.entireSessionRows.length && td > existing.td) {
              // No "Entire Session" row seen for this player yet — fall back to the old
              // highest-TD-wins pick (covers CSV shapes without a summary row).
              existing.row = row; existing.td = td;
            }
          }
        });

        const newSessions = Object.values(sessionMap).map(sess => {
          const isMatch = Object.values(sess.playerMap).some(pe =>
            (pe.drillRows || []).some(r => {
              const dt = (r['Drill Title'] || '').trim().toLowerCase();
              // Optional numeric suffix so non-standard formats split into more than 2
              // segments (e.g. "Match-First Half-Full-1"/"-2" for a 4x30 match) are still
              // recognized as a match, not just the standard 2x45 "-Full" title.
              return /^match-(first|second) half-full(-\d+)?$/.test(dt);
            })
          );
          const sessionType = isMatch ? 'match' : 'training';
          const opponent = opponents.find(o => o.name.toLowerCase() === sess.title.toLowerCase());
          const opponentId = opponent?.id || null;

          const playerEntries = Object.values(sess.playerMap).map(({ row, drillRows, entireSessionRows }) => {
            const fn = (row['Player First Name'] || '').trim();
            const ln = (row['Player Last Name']  || '').trim();
            const fullName = `${fn} ${ln}`.trim();
            const player = resolvePlayer(fullName);
            const sc = scenarios.find(s =>
              (s.playerId === 'team' || s.playerId === String(player?.id)) &&
              parseDate(sess.date) >= parseDate(s.startDate)
            );
            const mdLabel = sc ? getMDForDate(sc, sess.date) : null;
            const pos     = player?.position || null;
            const rawTargets = sc && mdLabel && pos ? allTargets[sc.scenario]?.[mdLabel]?.[pos] ?? null : null;
            // MD (match day) is never intensity-scaled — store the 90-min base so
            // MatchPerformance can compare against it using actual minutes played.
            const intensity = (mdLabel === 'MD') ? 1.0 : (sc?.intensity ?? 100) / 100;
            const scaledTargets = rawTargets && intensity !== 1
              ? Object.fromEntries(Object.entries(rawTargets).map(([m, { upper, lower }]) => [m, { upper: Math.round(upper * intensity), lower: Math.round(lower * intensity) }]))
              : rawTargets;
            // A manual day-specific override (set when planning the scenario) takes
            // full precedence over the computed/intensity-scaled targets.
            const dayOverride = sc?.target_overrides?.[convertDate(sess.date)]?.[pos];
            const targets = dayOverride || scaledTargets;
            // Use the canonical player name when resolved (direct or via alias) so
            // an alias like "Tristan van Gils" never reaches stored data/reports —
            // only the true players.name ("Tristan van Gilst") does.
            const displayName = player?.name || fullName;
            return {
              name: displayName, playerId: player?.id || null, position: pos,
              scenario: sc?.scenario || null, mdLabel, targets,
              metrics: buildMetricsFromRows((entireSessionRows && entireSessionRows.length) ? entireSessionRows : [row]),
              drillEntries: (drillRows || []).map(dr => ({
                playerName: displayName,
                drillTitle: (dr['Drill Title'] || '').trim(),
                td:        parseFloat(dr['Total Distance']) || 0,
                rd:        parseFloat(dr['Distance Zone 4 - Zone 6 (Absolute)']) || 0,
                hid:       parseFloat(dr['High Speed Running (Absolute)']) || 0,
                sd:        parseFloat(dr['Distance Zone 6 (Absolute)']) || 0,
                sa:        (parseInt(dr['Accelerations (Absolute)']) || 0) + (parseInt(dr['Decelerations (Absolute)']) || 0),
                expl:      parseFloat(dr['Explosive Distance (Absolute)']) || 0,
                hmld:      parseFloat(dr['HML Distance']) || 0,
                maxSpeed:  parseFloat(dr['Max Speed']) || 0,
                totalTime: (dr['Total Time'] || '').trim(),
                avgHR:     Math.round(parseFloat(dr['Average Heart Rate']) || 0) || null,
                maxHR:     Math.round(parseFloat(dr['Max Heart Rate']) || 0) || null,
              })),
            };
          });
          return { date: sess.date, title: sess.title, sessionType, opponentId, players: playerEntries, id: Date.now() + Math.random() };
        });

        try {
          // A session already existing for this date+title is either a genuine correction
          // re-upload (replace — the long-standing default, e.g. fixing a mistake in the same
          // file) or a second real session sharing the same label (2026-07-30 — confirmed case:
          // two separate MD-2 sessions run on one day) that needs adding on top instead. The
          // client can't tell those apart automatically, so when it detects the conflict it
          // asks — silently guessing either way risks either losing a correction or double-
          // counting load.
          const hasConflict = newSessions.some(sess =>
            sessions.some(s => s.date === convertDate(sess.date) && s.title === sess.title)
          );
          const sumExisting = hasConflict && window.confirm(
            'A session already exists for this date/title.\n\n' +
            'Click OK to ADD this upload\'s load on top of the existing data (use this when two ' +
            'separate sessions share the same label, e.g. two MD-2 sessions on one day).\n\n' +
            'Click Cancel to REPLACE the existing data instead (the normal re-upload/correction behavior).'
          );

          for (const sess of newSessions) {
            const result = await saveGPS({
              date: convertDate(sess.date),
              title: sess.title,
              scenario: sess.players[0]?.scenario || null,
              sessionType: sess.sessionType,
              opponentId: sess.opponentId,
              skip_notification: !sendNotifications,
              sum_existing: sumExisting,
              players: sess.players.map(p => ({
                playerId: p.playerId,
                name: p.name,
                position: p.position,
                mdLabel: p.mdLabel,
                td:         p.metrics.TD,
                rd:         p.metrics.RD,
                hid:        p.metrics.HID,
                sd:         p.metrics.SD,
                sa:         p.metrics.SA,
                expl:       p.metrics.Expl,
                hmld:       p.metrics.HMLD,
                max_speed:  p.metrics.maxSpeed,
                player_max_speed: p.metrics.playerMaxSpeed,
                minutes_played: p.metrics.minutesPlayed,
                avg_hr:     p.metrics.avgHR,
                max_hr:     p.metrics.maxHR,
                dist_per_min: p.metrics.distPerMin,
                sprints:    p.metrics.sprints,
                hr_z1:      p.metrics.hrZ1,
                hr_z2:      p.metrics.hrZ2,
                hr_z3:      p.metrics.hrZ3,
                hr_z4:      p.metrics.hrZ4,
                hr_z5:      p.metrics.hrZ5,
                hr_z6:      p.metrics.hrZ6,
              })),
            });
            const sessionId = result?.sessionId;
            if (sessionId) {
              const allDrills = sess.players.flatMap(p =>
                (p.drillEntries || []).map(d => ({
                  session_id: sessionId,
                  player_id:  p.playerId,
                  player_name: d.playerName,
                  drill_title: d.drillTitle,
                  td: d.td, rd: d.rd, hid: d.hid, sd: d.sd,
                  sa: d.sa, expl: d.expl, hmld: d.hmld,
                  max_speed: d.maxSpeed, total_time: d.totalTime,
                  avg_hr: d.avgHR, max_hr: d.maxHR,
                }))
              );
              if (allDrills.length > 0) {
                try { await saveDrillBatch(allDrills); } catch (e) {
                  console.warn('Drill save failed:', e.message);
                  alert(`Drill-level data failed to save for "${sess.title}" (${sess.date}): ${e.message}\n\nThe session's overall totals were still saved.`);
                }
              }
            }
          }
          const updated = await getGPS();
          setSessions(updated.map(sess => ({
            ...sess,
            date: sess.session_date?.slice(0, 10),
            title: sess.session_title,
            players: sess.players.map(p => ({
              ...p,
              playerId: p.player_id,
              mdLabel: p.md_label,
              metrics: {
                TD: p.td, RD: p.rd, HID: p.hid, SD: p.sd,
                SA: p.sa, Expl: p.expl, HMLD: p.hmld,
                maxSpeed: p.max_speed, avgHR: p.avg_hr,
                maxHR: p.max_hr, distPerMin: p.dist_per_min,
                sprints: p.sprints, hrZ1: p.hr_z1, hrZ2: p.hr_z2,
                hrZ3: p.hr_z3, hrZ4: p.hr_z4, hrZ5: p.hr_z5, hrZ6: p.hr_z6,
              },
            })),
          })));
        } catch (err) {
          console.error('Failed to save GPS session:', err);
          alert(`Upload failed: ${err?.message || 'unknown error'}\n\nAny sessions processed before this point in the file were saved; anything after was not.`);
        }

        setUploadStatus('success');
        setTimeout(() => setUploadStatus(null), 3000);
        e.target.value = '';
      } catch(err) {
        console.log('Error:', err.message);
        setUploadStatus('error');
      }
    };
    reader.onerror = () => setUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // Maps the GK-specific CSV export (separate from the Statsports team export
  // above) into gk_sessions rows. Only "Entire Session" rows are kept — the
  // file also contains per-drill breakdown rows whose metrics are already
  // folded into that summary row.
  const GK_COLUMN_MAP = {
    'Player Name': 'player_name',
    'Session Date': 'session_date',
    'Drill Title': 'drill_title',
    'Session Title': 'session_title',
    'Dives': 'dives',
    'Dives Left': 'dives_left',
    'Dives Right': 'dives_right',
    'Average GK Power': 'avg_gk_power',
    'GK Load': 'gk_load',
    'Average Time Since Last Dive': 'avg_time_since_last_dive',
    'Total Distance': 'td',
    'Distance Zone 4 - Zone 6 (Absolute)': 'rd',
    'High Speed Running (Absolute)': 'hid',
    'Accelerations (Absolute)': 'acc',
    'Decelerations (Absolute)': 'dec',
  };

  const handleGkCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setGkUploadStatus('processing');
    setGkUploadResult(null);
    const convertDate = str => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
      }
      return str;
    };
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const lines = evt.target.result.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
        const rawRows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.replace(/"/g, '').trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
          return obj;
        });
        const mapped = rawRows
          .map(raw => {
            const row = {};
            Object.entries(GK_COLUMN_MAP).forEach(([csvCol, field]) => { row[field] = raw[csvCol]; });
            row.session_date = convertDate(row.session_date);
            return row;
          })
          .filter(row => (row.drill_title || '').toLowerCase().includes('entire session'));
        const result = await uploadGkSessions(mapped);
        setGkUploadStatus('success');
        setGkUploadResult(result);
        const updatedGk = await getGkSessions();
        setGkSessions(groupGkSessions(updatedGk));
        e.target.value = '';
      } catch (err) {
        console.log('GK upload error:', err.message);
        setGkUploadStatus('error');
      }
    };
    reader.onerror = () => setGkUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // KNVB per-match player-stats CSV -> technical_match_stats (2026-08-17) — the sole Technical
  // Stats upload path now (SciSports's own handler/card were removed here when the club fully
  // switched sources; the backend importer is kept for historical backfill only, see
  // technicalImport.js). Same "no column remapping, send the raw parsed rows through as-is"
  // approach the old SciSports handler used — the backend's ingestKnvbCsv reads KNVB's own
  // column names (player_name, match_date, tackle/tackle_suc, etc.) directly.
  const handleTechKnvbCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setTechKnvbUploadStatus('processing');
    setTechKnvbUploadResult(null);
    setTechKnvbUploadError('');
    setResolvedAliasNamesKnvb(new Set());
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const lines = evt.target.result.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
        const rows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.replace(/"/g, '').trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
          return obj;
        });
        const result = await uploadTechnicalStatsKnvb(rows);
        setTechKnvbUploadStatus('success');
        setTechKnvbUploadResult(result);
        loadTechnicalStats();
        e.target.value = '';
      } catch (err) {
        setTechKnvbUploadStatus('error');
        setTechKnvbUploadError(err.message || 'Error reading file.');
      }
    };
    reader.onerror = () => setTechKnvbUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // ForceDecks (CMJ) CSV -> test_cmj, the same table/columns the manual Physical Test Input
  // CMJ form writes to (see server.production.js's /api/cmj/upload). A ForceDecks export mixes
  // every test type the device ran that session in one file and pads several header names with
  // a trailing space (e.g. "Jump Height (Imp-Mom) [cm] ") — transformHeader trims that before
  // any lookup, and only rows where Test Type is "CMJ" (case-insensitive) are kept, everything
  // else in the file is ignored.
  const handleCmjCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setCmjUploadStatus('processing');
    setCmjUploadResult(null);
    setResolvedCmjAliasNames(new Set());
    const convertDate = str => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      return str;
    };
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim(),
      complete: async results => {
        try {
          const rows = (results.data || [])
            .filter(row => (row['Test Type'] || '').trim().toLowerCase() === 'cmj')
            .map(row => ({
              name: (row['Name'] || '').trim(),
              weight: parseFloat(row['BW [KG]']) || null,
              height_cm: parseFloat(row['Jump Height (Imp-Mom) [cm]']) || null,
              peak_power_wkg: parseFloat(row['Peak Power / BM [W/kg]']) || null,
              test_date: convertDate(row['Date']),
            }))
            .filter(row => row.name);
          const result = await uploadCmjTests(rows);
          setCmjUploadStatus('success');
          setCmjUploadResult(result);
          loadCmjTests();
          e.target.value = '';
        } catch (err) {
          console.error('CMJ upload error:', err.message);
          setCmjUploadStatus('error');
        }
      },
      error: () => setCmjUploadStatus('error'),
    });
  };

  const activePlayers = players.filter(p => p.active);
  const cmjGroups = useMemo(() => groupCmjTests(cmjTests), [cmjTests]);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>GPS & Load — Upload</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Configure your scenario and import Statsports CSV files.</p>
      </div>

      {/* Scenario config */}
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Settings size={18} color="#3b82f6" />
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Active Scenarios</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowScenarioMap(true)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><MapIcon size={14} /> View</button>
            <button
              onClick={() => { setShowTemplatePicker(p => !p); setShowForm(false); setShowEditPicker(false); }}
              style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${showTemplatePicker ? '#8b5cf6' : 'var(--border-strong)'}`, background: showTemplatePicker ? 'rgba(139,92,246,0.1)' : 'var(--bg-app)', color: showTemplatePicker ? '#a78bfa' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
            ><BookOpen size={14} /> Templates</button>
            {scenarios.length > 0 && (
              <button
                onClick={() => { setShowForm(false); setShowEditPicker(p => !p); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${showEditPicker ? '#3b82f6' : 'var(--border-strong)'}`, background: showEditPicker ? 'rgba(59,130,246,0.1)' : 'var(--bg-app)', color: showEditPicker ? '#3b82f6' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Pencil size={14} /> Edit existing
              </button>
            )}
            <button onClick={openAdd} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ Add Scenario</button>
          </div>
        </div>

        {/* Template picker */}
        {showTemplatePicker && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Scenario Templates</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setTemplateEditor('new'); setShowTemplatePicker(false); }}
                  style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >+ New Template</button>
                <button onClick={() => setShowTemplatePicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
              </div>
            </div>
            {customTemplates.length === 0 ? (
              <p style={{ color: 'var(--text-dimmed)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>No templates yet. Create your first one.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {customTemplates.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: '#a78bfa', fontSize: 15 }}>{t.name}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.days?.length ?? 0} days · {t.days?.join(', ')}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => { setTemplateEditor(t); setShowTemplatePicker(false); }}
                        style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                      ><Pencil size={12} /> Edit</button>
                      <button
                        onClick={() => handleDeleteTemplate(t.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                      ><X size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit existing scenario picker */}
        {showEditPicker && !showForm && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Select a scenario to edit</h3>
              <button onClick={() => setShowEditPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {scenarios.map(sc => {
                const playerName = sc.playerId === 'team' ? 'Whole team' : players.find(p => String(p.id) === String(sc.playerId))?.name || 'Unknown';
                return (
                  <button
                    key={sc.id}
                    onClick={() => openEdit(sc)}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                  >
                    <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 16, minWidth: 40 }}>{sc.scenario}</span>
                    {(sc.intensity ?? 100) !== 100 && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', padding: '2px 7px', borderRadius: 5 }}>{sc.intensity}%</span>
                    )}
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Started {sc.startDate}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>→ {playerName}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Pencil size={12} /> Edit
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>{editingId ? 'Edit Scenario' : 'New Scenario'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Scenario</label>
                <select value={form.scenario} onChange={e => setForm(f => ({ ...f, scenario: e.target.value }))} style={inputStyle}>
                  {allScenarioNames.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Applies to</label>
                <select value={form.playerId} onChange={e => setForm(f => ({ ...f, playerId: e.target.value }))} style={inputStyle}>
                  <option value="team">Whole team</option>
                  {activePlayers.sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Start date (= day 1 of scenario)</label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>Load intensity</span>
                <span style={{ color: form.intensity === 100 ? 'var(--text-muted)' : '#f59e0b', fontWeight: 600 }}>{form.intensity ?? 100}%</span>
              </label>
              <input
                type="range" min={50} max={120} step={5}
                value={form.intensity ?? 100}
                onChange={e => setForm(f => ({ ...f, intensity: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: '#3b82f6', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
                <span>50%</span><span>75%</span><span>100%</span><span>120%</span>
              </div>
            </div>

            {previewDays.some(d => d.mdLabel === 'MD') && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                  Match duration <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>(affects MD targets only — scales + 10% warmup)</span>
                </label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[45, 60, 75, 90].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, mdMinutes: m }))}
                      style={{
                        padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${(form.mdMinutes ?? 90) === m ? '#3b82f6' : 'var(--border-strong)'}`,
                        background: (form.mdMinutes ?? 90) === m ? 'rgba(59,130,246,0.15)' : 'transparent',
                        color: (form.mdMinutes ?? 90) === m ? '#60a5fa' : 'var(--text-secondary)',
                      }}
                    >
                      {m} min
                    </button>
                  ))}
                  <input
                    type="number" min={1} max={120}
                    value={form.mdMinutes ?? 90}
                    onChange={e => setForm(f => ({ ...f, mdMinutes: parseInt(e.target.value) || 90 }))}
                    style={{ width: 60, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>min</span>
                </div>
              </div>
            )}

            {previewDays.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                  Day-by-day targets <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>(optional overrides for specific dates)</span>
                </label>
                <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
                  {previewDays.map(({ date, mdLabel }) => {
                    const hasOverride = !!form.targetOverrides?.[date];
                    const expanded = expandedOverrideDates.has(date);
                    const dateLabel = new Date(date).toLocaleDateString('nl-NL', { weekday: 'short', day: '2-digit', month: 'short' });
                    return (
                      <div key={date} style={{ borderTop: '1px solid var(--border)' }}>
                        <div
                          onClick={() => toggleOverrideExpanded(date)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: hasOverride ? 'rgba(245,158,11,0.06)' : 'transparent' }}
                        >
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', minWidth: 90 }}>{dateLabel}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 50 }}>{mdLabel}</span>
                          {hasOverride && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', borderRadius: 99, padding: '2px 8px' }}>Overridden</span>
                          )}
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 11, color: '#60a5fa' }}>{expanded ? 'Hide' : 'Edit targets'}</span>
                        </div>
                        {expanded && (
                          <div style={{ padding: '0 12px 12px' }}>
                            <DayMetricsTable
                              targets={getDayTargetsForForm(date, mdLabel)}
                              onChange={(pos, metric, bound, val) => updateOverrideCell(date, mdLabel, pos, metric, bound, val)}
                            />
                            {hasOverride && (
                              <button
                                type="button"
                                onClick={() => resetOverrideDay(date)}
                                style={{ marginTop: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}
                              >
                                Reset to scenario default
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={closeForm} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveForm} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{editingId ? 'Update Scenario' : 'Save Scenario'}</button>
            </div>
          </div>
        )}

        {scenarios.length === 0 && !showForm && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>No scenarios configured yet.</p>
        )}
        {scenarios.map(sc => {
          const playerName = sc.playerId === 'team' ? 'Whole team' : players.find(p => String(p.id) === String(sc.playerId))?.name || 'Unknown';
          return (
            <div key={sc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 16 }}>{sc.scenario}</span>
                {(sc.intensity ?? 100) !== 100 && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', background: '#451a03', padding: '2px 7px', borderRadius: 5 }}>{sc.intensity}%</span>
                )}
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Started {sc.startDate} · Day 1 = {allScenarioDays[sc.scenario]?.[0]}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>→ {playerName}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => openEdit(sc)} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => removeScenario(sc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CSV Upload */}
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Upload size={18} color="#10b981" />
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import Statsports CSV</h2>
          </div>
          <button onClick={() => setSendNotifications(v => !v)} title="Toggle push notifications for this upload"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: sendNotifications ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: 0 }}>
            {sendNotifications ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
            {sendNotifications ? 'Notifications on' : <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><BellOff size={13} /> Notifications off</span>}
          </button>
        </div>
        {!sendNotifications && (
          <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: '#451a03', color: '#fbbf24', fontSize: 12.5 }}>
            Players and staff won't be notified for sessions uploaded while this is off — useful when backfilling old matches.
          </div>
        )}
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('gps-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your Statsports CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Supports single or multi-session exports</p>
          <input id="gps-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCSV} />
        </div>
        {uploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {uploadStatus === 'success'    && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><CheckCircle size={18} /> Session imported successfully!</div>}
        {uploadStatus === 'error'      && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Shield size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import GK CSV</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('gk-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your GK session CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Only "Entire Session" rows are imported per player/session</p>
          <input id="gk-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleGkCSV} />
        </div>
        {gkUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {gkUploadStatus === 'success' && gkUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Imported {gkUploadResult.count} session row{gkUploadResult.count === 1 ? '' : 's'}.
            </div>
            {gkUploadResult.unmatched?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)', color: '#fca5a5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 4 }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported):
                </div>
                <div style={{ textAlign: 'center' }}>{gkUploadResult.unmatched.join(', ')}</div>
              </div>
            )}
          </div>
        )}
        {gkUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        {/* SciSports's own upload card was removed here 2026-08-17 — the club fully switched to
            KNVB as the Technical Stats source. The backend importer (ingestScisportsCsv) and its
            endpoint are kept working for historical backfill, just not offered in this UI
            anymore; see technicalImport.js's header comment. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <BarChart3 size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import Technical Stats CSV (KNVB)</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('technical-knvb-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse a KNVB player-stats CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>One file per match — Switch Play has no KNVB source and stays unscored for matches uploaded this way</p>
          <input id="technical-knvb-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleTechKnvbCSV} />
        </div>
        {techKnvbUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {techKnvbUploadStatus === 'success' && techKnvbUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Wrote {techKnvbUploadResult.rowsWritten} technical_match_stats row{techKnvbUploadResult.rowsWritten === 1 ? '' : 's'} for match #{techKnvbUploadResult.matchId}.
            </div>
            {techKnvbUploadResult.unmatchedPlayers?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#fca5a5' }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported) — opponent rows are expected here too:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {techKnvbUploadResult.unmatchedPlayers.map(name => (
                    resolvedAliasNamesKnvb.has(name) ? (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6ee7b7', padding: '6px 0' }}>
                        <CheckCircle size={13} /> {name} — aliased, re-upload this CSV to import their stats.
                      </div>
                    ) : (
                      <UnmatchedNameResolver
                        key={name}
                        name={name}
                        players={players}
                        onResolved={resolvedName => setResolvedAliasNamesKnvb(prev => new Set(prev).add(resolvedName))}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {techKnvbUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}><AlertTriangle size={18} /> {techKnvbUploadError || 'Error reading file.'}</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Activity size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import CMJ CSV (ForceDecks)</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('cmj-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your ForceDecks export</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Only "CMJ" test rows are imported — other test types in the file are ignored</p>
          <input id="cmj-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCmjCSV} />
        </div>
        {cmjUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {cmjUploadStatus === 'success' && cmjUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Imported {cmjUploadResult.count} CMJ result{cmjUploadResult.count === 1 ? '' : 's'}.
            </div>
            {cmjUploadResult.unmatchedPlayers?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#fca5a5' }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cmjUploadResult.unmatchedPlayers.map(name => (
                    resolvedCmjAliasNames.has(name) ? (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6ee7b7', padding: '6px 0' }}>
                        <CheckCircle size={13} /> {name} — aliased, re-upload this CSV to import their result.
                      </div>
                    ) : (
                      <UnmatchedNameResolver
                        key={name}
                        name={name}
                        players={players}
                        onResolved={resolvedName => setResolvedCmjAliasNames(prev => new Set(prev).add(resolvedName))}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {cmjUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}
      </div>

      {/* Imported sessions — GPS, GK, Technical Stats, CMJ */}
      {(sessions.length > 0 || gkSessions.length > 0 || technicalStatsSessions.length > 0 || cmjGroups.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Calendar size={18} color="#8b5cf6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>GPS Sessions ({sessions.length})</h2>
            </div>
            {sessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No GPS sessions yet.</div>
            ) : (
              [...sessions].sort((a, b) => new Date(b.date) - new Date(a.date)).map(s => (
                <div key={s.id} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.title || 'Session'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.date}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.players.length} players</span>
                    <button onClick={() => handleDeleteSession(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Shield size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>GK Sessions ({gkSessions.length})</h2>
            </div>
            {gkSessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No GK sessions yet.</div>
            ) : (
              gkSessions.map(s => (
                <div key={s.key} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.title || 'Session'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.date}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.players.length} player{s.players.length === 1 ? '' : 's'}</span>
                    <button onClick={() => handleDeleteGkSession(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <BarChart3 size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Technical Stats ({technicalStatsSessions.length})</h2>
            </div>
            {technicalStatsSessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No technical stats yet.</div>
            ) : (
              technicalStatsSessions.map(s => (
                <div key={s.match_id} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.opponent_name || s.session_title || 'Match'}</div>
                    {/* Source badge(s) — 2026-08-17, alongside the KNVB importer. A match showing
                        both means a second upload may have partially overwritten the first for
                        any KPI both sources cover (see technicalImport.js's ingestKnvbCsv comment). */}
                    {(s.sources || []).map(src => (
                      <span key={src} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, color: src === 'knvb' ? '#93c5fd' : '#fcd34d', background: src === 'knvb' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)' }}>
                        {src}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.session_date?.slice(0, 10)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.player_count} player{s.player_count === 1 ? '' : 's'} · {s.row_count} rows</span>
                    <button onClick={() => handleDeleteTechnicalStats(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Activity size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>CMJ Tests ({cmjGroups.length})</h2>
            </div>
            {cmjGroups.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No CMJ tests yet.</div>
            ) : (
              cmjGroups.map(g => (
                <div key={g.key} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>CMJ</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{g.date}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{g.players.length} player{g.players.length === 1 ? '' : 's'}</span>
                    <button onClick={() => handleDeleteCmjGroup(g)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Custom scenario templates list */}
      {customTemplates.length > 0 && (
        <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BookOpen size={18} color="#a78bfa" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Custom Scenario Templates</h2>
            </div>
            <button onClick={() => setTemplateEditor('new')} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ New Template</button>
          </div>
          {customTemplates.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#a78bfa', fontSize: 15 }}>{t.name}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t.days?.length ?? 0} days · {t.days?.join(', ')}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setTemplateEditor(t)} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><Pencil size={12} /> Edit</button>
                <button onClick={() => handleDeleteTemplate(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scenario template editor modal */}
      {templateEditor && (
        <ScenarioTemplateEditor
          editing={templateEditor === 'new' ? null : templateEditor}
          allBuiltInTargets={TARGETS}
          allBuiltInDays={SCENARIO_DAYS}
          allScenarioNames={allScenarioNames}
          onSave={handleSaveTemplate}
          onClose={() => setTemplateEditor(null)}
        />
      )}

      {/* Scenario map modal */}
      {showScenarioMap && (
        <ScenarioMapModal
          scenarioDays={allScenarioDays}
          customTemplates={customTemplates}
          onClose={() => setShowScenarioMap(false)}
        />
      )}
    </div>
  );
}